import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { resourcesApi, sessionsApi, accessLogsApi } from '../services/api';
import {
  Server,
  Users,
  ShieldCheck,
  AlertTriangle,
  LayoutDashboard,
} from 'lucide-react';
import { useAutoStartTour } from '../tours/useAutoStartTour';
import { sessionTone } from '@/lib/accessDecision';
import { Button, Card, EmptyState, StatCard, AreaChart, BarChart, PieChart, MonoCell, OutcomeBadge, PageHeader, Skeleton, Timestamp, TimeRangePicker, type StatCardAccent } from '@/components/ds-zta';
import { useUrlTimeRange } from '@/lib/useUrlState';
import { DEFAULT_RANGE, resolveRange } from '@/lib/timeRange';
import { describeWindow, filterToWindow, windowMayContinue, windowPhrase } from '@/features/audit/timeWindow';
import { bucketAccess } from '@/features/dashboard/accessBuckets';
import { isSessionLive } from '@/lib/sessionStatus';
import { BRAND } from '@/brand';

// How many rows each overview query reads. The API has no time-window or
// status filter for these lists, so every count below says what it covers.
// The header's time range (in the URL, design eval F26) narrows the access
// log sample in the browser; resources and live sessions are "now" and
// ignore it.
const RESOURCE_SAMPLE = 100;
const SESSION_SAMPLE = 50;
const LOG_SAMPLE = 100;

function ListSkeleton() {
  return (
    <div className="space-y-2" aria-busy="true">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="block" variant="rectangle" height={44} />
      ))}
    </div>
  );
}

// Counts only: hide the fractional ticks ECharts adds on a small range
// (the old chart's allowDecimals={false}).
const integerTick = (v: number) => (Number.isInteger(v) ? String(v) : '');

export default function Dashboard() {
  // Auto-launch the dashboard tour on the very first visit only.
  useAutoStartTour('dashboard');
  const [range, setRange] = useUrlTimeRange(DEFAULT_RANGE);

  const { data: resourcesData, isLoading: resourcesLoading, isError: resourcesError, refetch: refetchResources } = useQuery('dashboard-resources', () =>
    resourcesApi.listByRole(undefined, RESOURCE_SAMPLE, 0), {
    retry: false,
    onError: (error) => console.log('Resources API error:', error)
  });

  const { data: sessionsData, isLoading: sessionsLoading, isError: sessionsError, refetch: refetchSessions } = useQuery('dashboard-sessions', () =>
    sessionsApi.list(undefined, SESSION_SAMPLE, 0), {
    retry: false,
    onError: (error) => console.log('Sessions API error:', error)
  });

  // Active = status active and not past expires_at: the rule the Sessions page
  // uses, so a row it shows as Expired is not counted here.
  const sessionRows = sessionsData?.data.sessions || [];
  const activeSessions = sessionRows.filter((s) => isSessionLive(s));
  const sessionsTotal = sessionsData?.data.total ?? sessionRows.length;
  const sessionsSampled = sessionsTotal > sessionRows.length;

  const { data: accessLogsData, isLoading: logsLoading, isError: logsError, refetch: refetchLogs, dataUpdatedAt: logsUpdatedAt } = useQuery('dashboard-access-logs', () =>
    accessLogsApi.list(undefined, undefined, undefined, LOG_SAMPLE, 0), {
    retry: false,
    onError: (error) => console.log('Access logs API error:', error)
  });

  // Resolved per fetch, so "last 24 hours" moves with each refresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bounds = useMemo(() => resolveRange(range), [range, logsUpdatedAt]);
  const sampleLogs = useMemo(() => accessLogsData?.data.access_logs || [], [accessLogsData]);
  const windowLogs = useMemo(() => filterToWindow(sampleLogs, (l) => l.created_at, bounds), [sampleLogs, bounds]);
  // The newest LOG_SAMPLE rows reach back past the window's start, or not:
  // when not, the window holds more attempts than were read.
  const windowTruncated = windowMayContinue(sampleLogs, (l) => l.created_at, accessLogsData?.data.total ?? 0, bounds);
  const windowText = windowPhrase(range);

  // Only count core infrastructure resources (exclude cloud-discovered resources like azure_vm, aws_account, etc.)
  const coreResourceTypes = ['database', 'http_proxy', 'ssh', 'k8s', 'desktop', 'proxy'];
  const allResources = resourcesData?.data.resources || [];
  const coreResources = allResources.filter(r => coreResourceTypes.some(t => r.type?.includes(t)));
  const totalCoreResources = coreResources.length;
  const healthyResources = coreResources.filter(r => r.health_status === 'healthy').length;
  const unhealthyResources = coreResources.filter(r => r.health_status === 'unhealthy').length;
  const unknownResources = totalCoreResources - healthyResources - unhealthyResources;
  const resourcesSampled = (resourcesData?.data.total ?? 0) > allResources.length;
  // Counted from the sample inside the window. "N+" when the window holds
  // more attempts than the newest LOG_SAMPLE read.
  const totalAttempts = windowLogs.length;
  const failedAttempts = windowLogs.filter((l) => l.result !== 'allow').length;
  const countText = (n: number) => (windowTruncated ? `${n}+` : n);
  const sampleNote = windowTruncated ? `${windowText}, from the latest ${LOG_SAMPLE} read` : windowText;

  const stats = [
    {
      name: 'Total Resources',
      value: resourcesError ? '—' : totalCoreResources,
      icon: Server,
      trend: healthyResources > 0 ? 'up' : 'neutral',
      trendLabel: resourcesError ? 'could not load' : `${healthyResources} healthy${resourcesSampled ? ` (first ${RESOURCE_SAMPLE})` : ''}`,
      accent: 'neutral' as StatCardAccent,
    },
    {
      name: 'Active Sessions',
      value: sessionsError ? '—' : activeSessions.length,
      icon: Users,
      trend: activeSessions.length > 0 ? 'up' : 'neutral',
      trendLabel: sessionsError ? 'could not load' : sessionsSampled ? `in latest ${SESSION_SAMPLE} sessions` : 'currently active',
      accent: 'success' as StatCardAccent,
    },
    {
      name: 'Access Attempts',
      value: logsError ? '—' : countText(totalAttempts),
      icon: ShieldCheck,
      trend: totalAttempts > 0 ? 'up' : 'neutral',
      trendLabel: logsError ? 'could not load' : sampleNote,
      accent: 'neutral' as StatCardAccent,
    },
    {
      name: 'Denied Attempts',
      value: logsError ? '—' : countText(failedAttempts),
      icon: AlertTriangle,
      trend: !logsError && failedAttempts > 0 ? 'down' : 'neutral',
      trendLabel: logsError ? 'could not load' : failedAttempts > 0 ? `denied ${sampleNote}` : `none denied ${sampleNote}`,
      accent: (!logsError && failedAttempts > 0 ? 'danger' : 'neutral') as StatCardAccent,
    },
  ];

  // Access timeline for the area chart: per hour (up to two days) or per day
  // over the header's window, from the newest LOG_SAMPLE rows. When those rows
  // all fall inside the window and more exist, the chart is missing older
  // attempts and says so.
  const { buckets: accessTimeline, unit: timelineUnit } = useMemo(
    () => bucketAccess(windowLogs, bounds),
    [windowLogs, bounds],
  );
  const timelineTruncated = windowTruncated;

  // Resource type breakdown
  const resourceBreakdown = useMemo(() => {
    const resources = resourcesData?.data.resources || [];
    const types = [
      { type: 'database', label: 'Database' },
      { type: 'http_proxy', label: 'HTTP Proxy' },
      { type: 'ssh', label: 'SSH' },
      { type: 'k8s', label: 'Kubernetes' },
    ];
    return types.map(t => ({
      ...t,
      count: resources.filter(r => r.type === t.type).length,
    }));
  }, [resourcesData]);

  // Health pie chart data
  const healthData = useMemo(() => [
    // The health badges' own tones (design eval P7): one word, one colour.
    { name: 'Healthy', value: healthyResources, color: 'var(--zta-series-healthy)' },
    { name: 'Unhealthy', value: unhealthyResources, color: 'var(--zta-series-unhealthy)' },
    { name: 'Unknown', value: unknownResources, color: 'var(--zta-series-unknown)' },
  ].filter(d => d.value > 0), [healthyResources, unhealthyResources, unknownResources]);

  return (
    <div className="animate-fade-in">
      <PageHeader
        className="mb-8"
        icon={<LayoutDashboard />}
        title="Dashboard"
        description={`Overview of your ${BRAND.name} workspace`}
        timeRange={<TimeRangePicker value={range} onChange={setRange} label="Access activity time range" />}
      />

      {/* ── Stat Cards ───────────────────────────────────── */}
      <div data-tour="dashboard-stats" className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {stats.map((stat) => (
          <StatCard
            key={stat.name}
            label={stat.name}
            value={<span className="tabular-nums">{stat.value}</span>}
            icon={<stat.icon />}
            accent={stat.accent}
            trend={
              stat.trend === 'up'
                ? { direction: 'up', value: stat.trendLabel, sentiment: 'positive' }
                : stat.trend === 'down'
                  ? { direction: 'down', value: stat.trendLabel, sentiment: 'negative' }
                  : { direction: 'flat', value: stat.trendLabel }
            }
          />
        ))}
      </div>

      {/* ── Charts Row ───────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Access Attempts Timeline */}
        <Card className="lg:col-span-2" data-tour="dashboard-timeline">
          <Card.Header
            title={`Access Attempts (${describeWindow(range)})`}
            description={timelineTruncated
              ? `Only the latest ${LOG_SAMPLE} attempts are shown; earlier attempts in this window are not counted.`
              : `Per ${timelineUnit}, from the latest ${LOG_SAMPLE} log entries. The access log API has no time filter, so the window is applied to those entries.`}
            divider={false}
          />
          <Card.Body>
            {logsError ? (
              <EmptyState
                icon={<AlertTriangle />}
                title="Could not load access attempts"
                description="The access log request failed."
                action={<Button size="sm" variant="secondary" onClick={() => refetchLogs()}>Retry</Button>}
              />
            ) : (
            <AreaChart
              aria-label={`Access attempts per ${timelineUnit}, ${describeWindow(range)}`}
              height={220}
              // No attempts in the window: the empty state, not an Allow and
              // a red Deny line drawn flat along 0 (design eval F47).
              data={accessTimeline.some((b) => b.allow + b.deny > 0) ? accessTimeline : []}
              loading={logsLoading}
              empty={logsLoading ? undefined : <EmptyState icon={<ShieldCheck />} title={`No access attempts ${windowText}`} />}
              xKey={(r) => r.label}
              showSymbols={false}
              yAxisFormat={integerTick}
              series={[
                { name: 'Allow', value: (r) => r.allow, color: 'var(--zta-series-allow)' },
                { name: 'Deny', value: (r) => r.deny, color: 'var(--zta-series-deny)' },
              ]}
            />
            )}
          </Card.Body>
        </Card>

        {/* Resource Health Donut */}
        <Card data-tour="dashboard-health">
          <Card.Header title="Resource Health" divider={false} />
          <Card.Body>
          {resourcesLoading ? (
            <ListSkeleton />
          ) : resourcesError ? (
            <EmptyState
              icon={<AlertTriangle />}
              title="Could not load resources"
              description="The resources request failed."
              action={<Button size="sm" variant="secondary" onClick={() => refetchResources()}>Retry</Button>}
            />
          ) : healthData.length > 0 ? (
            <div className="flex flex-col items-center">
              <PieChart
                aria-label="Resource health"
                className="w-full"
                height={180}
                data={healthData}
                innerRadius="58%"
                outerRadius="80%"
                showLegend={false}
              />
              <div className="flex items-center gap-4 mt-3">
                {healthData.map((d) => (
                  <div key={d.name} className="flex items-center gap-1.5 text-xs text-[var(--d2d-text-muted)]">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                    {d.name} ({d.value})
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[180px] text-sm text-[var(--d2d-text-muted)]">
              No resources registered
            </div>
          )}
          </Card.Body>
        </Card>
      </div>

      {/* ── Bottom Row ───────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Resources by Type */}
        <Card data-tour="dashboard-resources-by-type">
          <Card.Header title="Resources by Type" divider={false} />
          <Card.Body>
            {resourcesError ? (
              <EmptyState
                icon={<AlertTriangle />}
                title="Could not load resources"
                action={<Button size="sm" variant="secondary" onClick={() => refetchResources()}>Retry</Button>}
              />
            ) : (
            <BarChart
              aria-label="Resources by type"
              height={200}
              orientation="horizontal"
              categoryGutter={90}
              data={resourceBreakdown.some((r) => r.count > 0) ? resourceBreakdown : []}
              loading={resourcesLoading}
              empty={resourcesLoading ? undefined : <EmptyState icon={<Server />} title="No resources yet" />}
              xKey={(r) => r.label}
              yAxisFormat={integerTick}
              series={[{ name: 'Count', value: (r) => r.count }]}
            />
            )}
          </Card.Body>
        </Card>

        {/* Recent Access Logs */}
        <Card data-tour="dashboard-recent-access">
          <Card.Header title="Recent Access Attempts" description={`Latest ${windowText}`} divider={false} />
          <Card.Body>
          {logsLoading ? (
            <ListSkeleton />
          ) : logsError ? (
            <EmptyState
              icon={<AlertTriangle />}
              title="Could not load access attempts"
              description="The access log request failed."
              action={<Button size="sm" variant="secondary" onClick={() => refetchLogs()}>Retry</Button>}
            />
          ) : (
          <div className="space-y-2 max-h-[220px] overflow-y-auto">
            {windowLogs.slice(0, 8).map((log) => (
              <div
                key={log.id}
                className="flex items-center justify-between py-2 px-3 rounded-lg bg-[var(--d2d-bg-secondary)]"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex items-center justify-center h-7 w-7 rounded-full bg-[var(--ds-color-bg-subtle)] flex-shrink-0 text-label font-semibold text-[var(--d2d-text-secondary)] uppercase">
                    {log.user_id?.charAt(0) || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[var(--d2d-text-primary)] truncate">
                      <MonoCell value={log.user_id} copyable={false} />
                    </p>
                    <p className="text-micro text-[var(--d2d-text-muted)] truncate">
                      {log.action} · <Timestamp value={log.created_at} noSeconds />
                    </p>
                  </div>
                </div>
                <OutcomeBadge value={log.result === 'allow' ? 'allow' : 'deny'} decision>
                  {log.result === 'allow' ? 'Allow' : 'Deny'}
                </OutcomeBadge>
              </div>
            ))}
            {windowLogs.length === 0 && (
              <EmptyState icon={<ShieldCheck />} title={`No access attempts ${windowText}`} />
            )}
          </div>
          )}
          </Card.Body>
        </Card>

        {/* Active Sessions */}
        <Card data-tour="dashboard-active-sessions">
          <Card.Header title="Active Sessions" description="Live now; the time range does not apply" divider={false} />
          <Card.Body>
          {sessionsLoading ? (
            <ListSkeleton />
          ) : sessionsError ? (
            <EmptyState
              icon={<AlertTriangle />}
              title="Could not load sessions"
              description="The sessions request failed."
              action={<Button size="sm" variant="secondary" onClick={() => refetchSessions()}>Retry</Button>}
            />
          ) : (
          <div className="space-y-2 max-h-[220px] overflow-y-auto">
            {activeSessions.slice(0, 8).map((session) => (
              <div key={session.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-[var(--d2d-bg-secondary)]">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex items-center justify-center h-7 w-7 rounded-full bg-[var(--ds-color-bg-subtle)] flex-shrink-0 text-label font-semibold text-[var(--d2d-text-primary)] uppercase">
                    {session.user_id?.charAt(0) || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[var(--d2d-text-primary)] truncate">
                      {session.user_email || <MonoCell value={session.user_id} copyable={false} />}
                    </p>
                    <p className="text-micro text-[var(--d2d-text-muted)]">
                      {session.session_type} · <Timestamp value={session.started_at} noSeconds />
                    </p>
                  </div>
                </div>
                <OutcomeBadge value="active" tone={sessionTone('active')}>Active</OutcomeBadge>
              </div>
            ))}
            {activeSessions.length === 0 && (
              <EmptyState icon={<Users />} title="No active sessions" />
            )}
          </div>
          )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
